import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const MaintenanceContainer = styled.div`
    font-family: Inter;
    font-size: 16px;
    font-weight: 400;
    display: flex;
    flex-direction: column;
    background: var(--Secondary-Dark-1, #0C202A);
    justify-content: space-between;
    align-items: flex-start;
    padding: 24px;
    height: 100%;

    @media (min-width: 768px) {
        padding: 32px 72px;
    }

    @media (min-width: 1600px) {
        padding: 96px;
    }
`
const MaintenanceHeader = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;

    @media (max-width: 768px) {
        align-self: center;
    }
    
    & img {
        width: 51px;
    }

    & span {
        font-family: 'Poppins';
        color: white;
        font-weight: 500;
        font-size: 24px;
    }
`

const MaintenanceBody = styled.div`

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 48px;
    align-self: stretch;

    & h1 {
        color: var(--Primary-Red-Orange-1, #FB2F2F);
        font-family: Poppins;
        font-size: 96px;
        font-style: normal;
        font-weight: 700;
        line-height: 120px; /* 125% */
    }

    & p {
        color: var(--Secondary-Light-Grey-1, #DFDFDF);
        font-family: Inter;
        font-size: 24px;
        font-style: normal;
        font-weight: 500;
        line-height: 32px; /* 133.333% */
    }

    #timer-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }

    #timer-container p {
        color: var(--Secondary-Light-Grey-1, #DFDFDF);
        font-family: Inter;
        font-size: 20px;
        font-style: normal;
        font-weight: 700;
        line-height: 32px; /* 160% */
    }

    #timer-container .timer-container__wrapper {
        display: flex;
        align-items: flex-start;
        gap: 8px;
    }

    #timer-container .timer-container__wrapper span {
        color: var(--Primary-Light-Alabaster-1, #F5F5F5);
        text-align: center;
        font-family: Poppins;
        font-size: 56px;
        font-style: normal;
        font-weight: 700;
        line-height: 64px; /* 114.286% */
    }

    #timer-container .timer-container__wrapper span:not(.colon) {
        font-size: 64px;
        line-height: 72px;
    }

    #timer-container .timer-container__wrapper-text {
        display: flex;
        align-items: flex-start;
        gap: 36px;
        margin-top: 4px;
    }

    #timer-container .timer-container__wrapper-text span {
        width: 84px;

        color: var(--Secondary-Light-Grey-1, #DFDFDF);
        font-family: Inter;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 32px; /* 200% */
    }

    @media (max-width: 768px) {
   
        align-items: center;
        h1 {
            font-size: 48px;
            line-height: 56px;
            text-align: center;
        }

        p {
            font-size: 20px;
            line-height: 32px;
            text-align: center;
        }

        p br {
            display: none;
        }

        #timer-container {
            align-items: center;
        }

        #timer-container p,
        #timer-container .timer-container__wrapper-text span {
            text-align: center;
        }
        #timer-container .timer-container__wrapper,
        #timer-container .timer-container__wrapper-text {
            align-self: center;
        }
    }


`

const MaintenanceFooter = styled.div`

    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    align-self: stretch;

    & > div {
        display: flex;
        padding: 16px 0;
        align-items: center;
        gap: 16px;
    }

    & > div a {
        color: var(--Primary-Light-Alabaster-1, #F5F5F5);
        font-family: Poppins;
        font-size: 24px;
        font-style: normal;
        font-weight: 400;
        line-height: 48px; /* 200% */
        text-decoration: none;
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: center;
        
        & > div {
            padding: 0;
        }

        & > div, & > div a {
            font-size: 16px;
        }
    }
`

type CountdownProps = {
    nowDate: Date; // server now date
    endDate: string; // Format: "2025-07-27T10:30:00",
    logo?: string;
    name?: string;
    website?: string;
    id?: string;
};

export const Maintenance: React.FC<CountdownProps> = ({
    nowDate,
    endDate,
    logo,
    name,
    website,
    id
    }) => {
    const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);

    useEffect(() => {
        if (!nowDate || !endDate) {
          window.location.href = "/";
          return;
        }
    
        const now = new Date(nowDate);
        const end = new Date(endDate);
    
        if (isNaN(now.getTime()) || isNaN(end.getTime()) || now >= end) {
          window.location.href = "/";
          return;
        }
    
        let remaining = Math.floor((end.getTime() - now.getTime()) / 1000);
        setRemainingSeconds(remaining);
    
        const interval = setInterval(() => {
          remaining -= 1;
          setRemainingSeconds(remaining);
    
          if (remaining <= 0) {
            clearInterval(interval);
            window.location.href = "/";
          }
        }, 1000);
    
        return () => clearInterval(interval);
    }, [nowDate, endDate]);
    let hours = 0;
    let minutes = 0;
    if (remainingSeconds){
        hours = Math.floor(remainingSeconds / 3600);
        minutes = Math.floor((remainingSeconds % 3600) / 60);
    }
    return (
        <MaintenanceContainer>
            <MaintenanceHeader>
                {id && id.includes('cloudwaitress') ? <>
                <a className="navbar-brand" href="https://www.cloudwaitress.com">
                    <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52" fill="none">
                    <path d="M51.6698 26C51.6698 40.1385 40.1944 51.6 26.0388 51.6C11.8831 51.6 0.407715 40.1385 0.407715 26C0.407715 11.8615 11.8831 0.400024 26.0388 0.400024C40.1944 0.400024 51.6698 11.8615 51.6698 26Z" fill="#FB2F2F"/>
                    <path d="M22.0397 11.6567C18.6097 13.0145 18.8241 16.1589 19.0384 17.5881C16.2516 17.5167 14.751 18.0884 13.6076 19.6606C12.6091 21.0337 12.5358 23.3051 12.7501 24.3771C11.7497 24.3771 7.31934 26.0207 7.31934 31.5233C7.31934 35.9254 11.1066 38.3838 13.3933 38.5981H17.9666C18.4191 39.3366 19.9245 40.8135 22.3255 40.8135C24.7265 40.8135 26.2319 39.3366 26.6844 38.5981H38.1177C39.9279 38.5029 43.7628 37.1689 44.6203 32.5953C45.4778 28.0217 42.1193 25.4014 40.3329 24.6629C41.2618 23.3766 42.1193 19.669 39.5468 17.4452C36.174 14.5295 32.6631 16.4686 31.3292 17.8025C30.5431 16.4447 28.8567 13.6148 28.3994 13.1575C27.8277 12.5858 25.7568 10.1853 22.0397 11.6567Z" fill="#FFF3F3"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M28.544 26.9394C29.1331 26.3204 29.4947 25.4833 29.4947 24.5618C29.4947 22.6555 27.9474 21.1101 26.0388 21.1101C24.1302 21.1101 22.5829 22.6555 22.5829 24.5618C22.5829 25.4833 22.9444 26.3204 23.5335 26.9394L23.0344 27.7856C22.8029 27.5701 22.4924 27.4382 22.1509 27.4382C21.4352 27.4382 20.855 28.0177 20.855 28.7326C20.855 29.3104 21.2341 29.7998 21.7574 29.9662C21.0283 31.4688 22.1186 33.2629 23.835 33.2629H28.2426C29.9763 33.2629 31.0713 31.4324 30.2976 29.9208C30.7571 29.7218 31.0786 29.2647 31.0786 28.7326C31.0786 28.0177 30.4984 27.4382 29.7827 27.4382C29.4865 27.4382 29.2135 27.5374 28.9953 27.7044L28.544 26.9394Z" fill="#FB2F2F"/>
                    </svg>
                </a>
                <svg xmlns="http://www.w3.org/2000/svg" width="254" height="24" viewBox="0 0 254 24" fill="none">
                    <path d="M14.2759 15.764H18.9193C18.8261 17.2665 18.4068 18.6004 17.6614 19.7656C16.9263 20.9308 15.8962 21.8405 14.5709 22.4947C13.2561 23.1488 11.672 23.4759 9.8188 23.4759C8.36935 23.4759 7.07002 23.2306 5.92081 22.74C4.7716 22.2391 3.78804 21.5236 2.97013 20.5935C2.16258 19.6634 1.54656 18.539 1.12208 17.2205C0.697593 15.902 0.485352 14.425 0.485352 12.7896V11.2411C0.485352 9.60571 0.70277 8.12875 1.13761 6.81021C1.5828 5.48146 2.21434 4.35201 3.03225 3.42188C3.86051 2.49175 4.84925 1.77627 5.99846 1.27543C7.14767 0.774589 8.43147 0.52417 9.84986 0.52417C11.7342 0.52417 13.3234 0.86147 14.6175 1.53607C15.922 2.21067 16.9315 3.1408 17.6459 4.32646C18.3706 5.51212 18.8054 6.86132 18.9504 8.37406H14.2914C14.2396 7.47459 14.0585 6.71311 13.7479 6.08962C13.4373 5.4559 12.9662 4.98062 12.3346 4.66376C11.7134 4.33668 10.8852 4.17314 9.84986 4.17314C9.07337 4.17314 8.39523 4.31624 7.81545 4.60243C7.23567 4.88863 6.74907 5.32303 6.35564 5.90564C5.96222 6.48824 5.66715 7.22417 5.47044 8.11342C5.28408 8.99244 5.1909 10.0248 5.1909 11.2104V12.7896C5.1909 13.9446 5.27891 14.9616 5.45491 15.8407C5.63092 16.7095 5.9001 17.4454 6.26246 18.0484C6.63518 18.6413 7.11143 19.091 7.69121 19.3976C8.28135 19.694 8.99054 19.8423 9.8188 19.8423C10.792 19.8423 11.5944 19.6889 12.2259 19.3823C12.8575 19.0757 13.3389 18.6208 13.6702 18.0178C14.0119 17.4147 14.2138 16.6635 14.2759 15.764Z" fill="#F5F5F5"/>
                    <path d="M36.5147 19.5816V23.1693H25.1313V19.5816H36.5147ZM26.6377 0.846138V23.1693H21.9787V0.846138H26.6377Z" fill="#F5F5F5"/>
                    <path d="M56.7345 11.4864V12.5443C56.7345 14.241 56.5016 15.764 56.0357 17.1132C55.5698 18.4624 54.9124 19.6123 54.0634 20.5628C53.2144 21.5032 52.1998 22.2238 51.0196 22.7246C49.8496 23.2255 48.5503 23.4759 47.1216 23.4759C45.7032 23.4759 44.4038 23.2255 43.2236 22.7246C42.0536 22.2238 41.039 21.5032 40.1797 20.5628C39.3204 19.6123 38.6526 18.4624 38.1763 17.1132C37.7105 15.764 37.4775 14.241 37.4775 12.5443V11.4864C37.4775 9.77947 37.7105 8.25651 38.1763 6.91754C38.6422 5.56834 39.2997 4.41845 40.1486 3.46788C41.008 2.51731 42.0226 1.7916 43.1925 1.29076C44.3728 0.789921 45.6721 0.539502 47.0905 0.539502C48.5192 0.539502 49.8186 0.789921 50.9885 1.29076C52.1688 1.7916 53.1834 2.51731 54.0323 3.46788C54.8917 4.41845 55.5543 5.56834 56.0202 6.91754C56.4964 8.25651 56.7345 9.77947 56.7345 11.4864ZM52.029 12.5443V11.4558C52.029 10.2701 51.9203 9.22753 51.7029 8.32806C51.4854 7.4286 51.1645 6.67223 50.74 6.05895C50.3155 5.44568 49.7979 4.98573 49.187 4.67909C48.5762 4.36223 47.8773 4.2038 47.0905 4.2038C46.3036 4.2038 45.6048 4.36223 44.994 4.67909C44.3935 4.98573 43.881 5.44568 43.4565 6.05895C43.0424 6.67223 42.7266 7.4286 42.5092 8.32806C42.2918 9.22753 42.1831 10.2701 42.1831 11.4558V12.5443C42.1831 13.7197 42.2918 14.7623 42.5092 15.672C42.7266 16.5715 43.0476 17.3329 43.472 17.9564C43.8965 18.5697 44.4142 19.0348 45.025 19.3516C45.6359 19.6685 46.3347 19.8269 47.1216 19.8269C47.9084 19.8269 48.6072 19.6685 49.2181 19.3516C49.8289 19.0348 50.3414 18.5697 50.7555 17.9564C51.1697 17.3329 51.4854 16.5715 51.7029 15.672C51.9203 14.7623 52.029 13.7197 52.029 12.5443Z" fill="#F5F5F5"/>
                    <path d="M72.575 0.846138H77.2184V15.5953C77.2184 17.3125 76.8457 18.7588 76.1003 19.9342C75.3652 21.1097 74.3454 21.9938 73.0409 22.5866C71.7468 23.1795 70.2559 23.4759 68.5683 23.4759C66.8807 23.4759 65.3795 23.1795 64.0646 22.5866C62.7601 21.9938 61.7352 21.1097 60.9897 19.9342C60.2547 18.7588 59.8871 17.3125 59.8871 15.5953V0.846138H64.5461V15.5953C64.5461 16.597 64.7066 17.4147 65.0275 18.0484C65.3484 18.6821 65.8092 19.1472 66.4097 19.4436C67.0205 19.74 67.7401 19.8882 68.5683 19.8882C69.4173 19.8882 70.1368 19.74 70.727 19.4436C71.3275 19.1472 71.783 18.6821 72.0936 18.0484C72.4145 17.4147 72.575 16.597 72.575 15.5953V0.846138Z" fill="#F5F5F5"/>
                    <path d="M87.9962 23.1693H83.0732L83.1043 19.5816H87.9962C89.2179 19.5816 90.248 19.3107 91.0866 18.769C91.9252 18.2171 92.5568 17.4147 92.9813 16.3619C93.4161 15.3091 93.6335 14.0366 93.6335 12.5443V11.4558C93.6335 10.311 93.5093 9.30419 93.2608 8.43538C93.0227 7.56658 92.6655 6.83577 92.1892 6.24294C91.713 5.65011 91.128 5.20548 90.4344 4.90907C89.7407 4.60243 88.9435 4.44911 88.0428 4.44911H82.98V0.846138H88.0428C89.5543 0.846138 90.9365 1.10167 92.1892 1.61273C93.4523 2.11357 94.5446 2.83416 95.466 3.77451C96.3875 4.71487 97.0967 5.8392 97.5936 7.14751C98.1009 8.44561 98.3546 9.89191 98.3546 11.4864V12.5443C98.3546 14.1286 98.1009 15.5749 97.5936 16.8832C97.0967 18.1915 96.3875 19.3159 95.466 20.2562C94.555 21.1863 93.4627 21.9069 92.1892 22.418C90.9261 22.9188 89.5285 23.1693 87.9962 23.1693ZM85.7133 0.846138V23.1693H81.0543V0.846138H85.7133Z" fill="#F5F5F5"/>
                    <path d="M107.191 19.6276L111.695 0.846138H114.211L114.785 3.97383L109.986 23.1693H107.284L107.191 19.6276ZM104.877 0.846138L108.604 19.6276L108.294 23.1693H105.281L100.265 0.846138H104.877ZM118.745 19.5509L122.426 0.846138H127.038L122.038 23.1693H119.025L118.745 19.5509ZM115.624 0.846138L120.159 19.7043L120.034 23.1693H117.332L112.502 3.9585L113.124 0.846138H115.624Z" fill="#F5F5F5"/>
                    <path d="M138.08 4.66376L131.93 23.1693H126.976L135.378 0.846138H138.53L138.08 4.66376ZM143.189 23.1693L137.024 4.66376L136.527 0.846138H139.711L148.159 23.1693H143.189ZM142.91 14.8594V18.4624H130.967V14.8594H142.91Z" fill="#F5F5F5"/>
                    <path d="M155.209 0.846138V23.1693H150.566V0.846138H155.209Z" fill="#F5F5F5"/>
                    <path d="M169.217 0.846138V23.1693H164.574V0.846138H169.217ZM176.175 0.846138V4.44911H157.725V0.846138H176.175Z" fill="#F5F5F5"/>
                    <path d="M178.83 0.846138H187.263C188.992 0.846138 190.478 1.10167 191.72 1.61273C192.973 2.12379 193.936 2.88016 194.609 3.88184C195.282 4.88351 195.618 6.11517 195.618 7.5768C195.618 8.77269 195.411 9.79992 194.997 10.6585C194.593 11.5069 194.019 12.2172 193.273 12.7896C192.538 13.3518 191.674 13.8015 190.68 14.1388L189.204 14.9054H181.874L181.843 11.3178H187.294C188.112 11.3178 188.79 11.1747 189.329 10.8885C189.867 10.6023 190.271 10.2037 190.54 9.69259C190.819 9.18153 190.959 8.5887 190.959 7.9141C190.959 7.19862 190.825 6.58024 190.555 6.05895C190.286 5.53767 189.877 5.13905 189.329 4.86307C188.78 4.5871 188.091 4.44911 187.263 4.44911H183.489V23.1693H178.83V0.846138ZM191.472 23.1693L186.316 13.2189L191.239 13.1882L196.457 22.9546V23.1693H191.472Z" fill="#F5F5F5"/>
                    <path d="M214.378 19.5816V23.1693H202.343V19.5816H214.378ZM203.865 0.846138V23.1693H199.206V0.846138H203.865ZM212.81 9.9379V13.4336H202.343V9.9379H212.81ZM214.363 0.846138V4.44911H202.343V0.846138H214.363Z" fill="#F5F5F5"/>
                    <path d="M228.868 17.3278C228.868 16.9292 228.806 16.5715 228.681 16.2546C228.567 15.9275 228.35 15.6311 228.029 15.3654C227.708 15.0894 227.258 14.8185 226.678 14.5528C226.098 14.287 225.348 14.0111 224.426 13.7249C223.401 13.3978 222.428 13.0298 221.506 12.621C220.595 12.2121 219.788 11.7368 219.084 11.1951C218.39 10.6432 217.841 10.0043 217.438 9.27863C217.044 8.55293 216.848 7.70968 216.848 6.74889C216.848 5.81876 217.055 4.97551 217.469 4.21914C217.883 3.45255 218.463 2.79839 219.208 2.25666C219.954 1.70472 220.834 1.28054 221.848 0.984124C222.873 0.687709 223.996 0.539502 225.218 0.539502C226.885 0.539502 228.34 0.835917 229.582 1.42875C230.824 2.02158 231.787 2.83416 232.471 3.8665C233.164 4.89885 233.511 6.0794 233.511 7.40815H228.883C228.883 6.754 228.743 6.18161 228.464 5.69099C228.195 5.19015 227.781 4.79663 227.221 4.51044C226.673 4.22425 225.979 4.08115 225.14 4.08115C224.333 4.08115 223.66 4.2038 223.122 4.44911C222.583 4.6842 222.179 5.00617 221.91 5.41502C221.641 5.81364 221.506 6.26338 221.506 6.76422C221.506 7.1424 221.6 7.48481 221.786 7.79145C221.983 8.09808 222.273 8.38428 222.656 8.65003C223.039 8.91578 223.51 9.1662 224.069 9.40129C224.628 9.63638 225.275 9.86635 226.01 10.0912C227.242 10.4592 228.324 10.8731 229.256 11.3331C230.198 11.7931 230.985 12.3092 231.616 12.8816C232.248 13.454 232.724 14.103 233.045 14.8288C233.366 15.5545 233.527 16.3773 233.527 17.2972C233.527 18.2682 233.335 19.137 232.952 19.9036C232.569 20.6702 232.015 21.3192 231.29 21.8507C230.566 22.3822 229.701 22.786 228.697 23.0619C227.693 23.3379 226.569 23.4759 225.327 23.4759C224.209 23.4759 223.106 23.3328 222.019 23.0466C220.932 22.7502 219.943 22.3056 219.053 21.7127C218.173 21.1199 217.469 20.3635 216.941 19.4436C216.413 18.5237 216.149 17.4352 216.149 16.1779H220.823C220.823 16.873 220.932 17.4607 221.149 17.9411C221.367 18.4215 221.672 18.8099 222.066 19.1063C222.469 19.4027 222.946 19.6174 223.494 19.7503C224.053 19.8831 224.664 19.9496 225.327 19.9496C226.134 19.9496 226.797 19.8371 227.315 19.6123C227.843 19.3874 228.231 19.0757 228.479 18.677C228.738 18.2784 228.868 17.8287 228.868 17.3278Z" fill="#F5F5F5"/>
                    <path d="M248.466 17.3278C248.466 16.9292 248.404 16.5715 248.28 16.2546C248.166 15.9275 247.949 15.6311 247.628 15.3654C247.307 15.0894 246.856 14.8185 246.277 14.5528C245.697 14.287 244.946 14.0111 244.025 13.7249C243 13.3978 242.027 13.0298 241.105 12.621C240.194 12.2121 239.387 11.7368 238.683 11.1951C237.989 10.6432 237.44 10.0043 237.036 9.27863C236.643 8.55293 236.446 7.70968 236.446 6.74889C236.446 5.81876 236.653 4.97551 237.067 4.21914C237.482 3.45255 238.061 2.79839 238.807 2.25666C239.552 1.70472 240.432 1.28054 241.447 0.984124C242.472 0.687709 243.595 0.539502 244.817 0.539502C246.484 0.539502 247.938 0.835917 249.181 1.42875C250.423 2.02158 251.386 2.83416 252.069 3.8665C252.763 4.89885 253.11 6.0794 253.11 7.40815H248.482C248.482 6.754 248.342 6.18161 248.063 5.69099C247.793 5.19015 247.379 4.79663 246.82 4.51044C246.271 4.22425 245.578 4.08115 244.739 4.08115C243.932 4.08115 243.259 4.2038 242.72 4.44911C242.182 4.6842 241.778 5.00617 241.509 5.41502C241.24 5.81364 241.105 6.26338 241.105 6.76422C241.105 7.1424 241.198 7.48481 241.385 7.79145C241.581 8.09808 241.871 8.38428 242.254 8.65003C242.637 8.91578 243.109 9.1662 243.668 9.40129C244.227 9.63638 244.874 9.86635 245.609 10.0912C246.841 10.4592 247.923 10.8731 248.855 11.3331C249.797 11.7931 250.584 12.3092 251.215 12.8816C251.847 13.454 252.323 14.103 252.644 14.8288C252.965 15.5545 253.125 16.3773 253.125 17.2972C253.125 18.2682 252.934 19.137 252.551 19.9036C252.168 20.6702 251.614 21.3192 250.889 21.8507C250.164 22.3822 249.3 22.786 248.296 23.0619C247.291 23.3379 246.168 23.4759 244.926 23.4759C243.807 23.4759 242.705 23.3328 241.618 23.0466C240.531 22.7502 239.542 22.3056 238.651 21.7127C237.771 21.1199 237.067 20.3635 236.539 19.4436C236.011 18.5237 235.747 17.4352 235.747 16.1779H240.422C240.422 16.873 240.531 17.4607 240.748 17.9411C240.965 18.4215 241.271 18.8099 241.664 19.1063C242.068 19.4027 242.544 19.6174 243.093 19.7503C243.652 19.8831 244.263 19.9496 244.926 19.9496C245.733 19.9496 246.396 19.8371 246.913 19.6123C247.441 19.3874 247.83 19.0757 248.078 18.677C248.337 18.2784 248.466 17.8287 248.466 17.3278Z" fill="#F5F5F5"/>
                    </svg>
                </> :
                <>
                    <a href={website}>
                        <img src={logo} width={52}/>
                    </a>
                    <span>{name}</span>
                </>
                }
            </MaintenanceHeader>
            <MaintenanceBody>
                <h1>We'll Be Right Back!</h1>
                <p>Our system is currently undergoing scheduled maintenance. We’re working <br /> hard to make your experience even better!</p>
                <div id="timer-container">
                    <p>Estimated Time Remaining</p>
                    <div className="timer-container__wrapper">    
                        <span className="hour" id="hour">{String(hours).padStart(2, '0')}</span>
                        <span className="colon">:</span>
                        <span className="minute" id="minute">{String(minutes).padStart(2, '0')}</span>
                    </div>
                    <div className="timer-container__wrapper-text">
                        <span>Hours</span>
                        <span>Minutes</span>
                    </div>
                </div>
            </MaintenanceBody>
            <MaintenanceFooter>
            {/* <div>
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <mask id="mask0_2001_190" style={{maskType: 'alpha'}} maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
                    <rect width="32" height="32" fill="#D9D9D9"/>
                </mask>
                <g mask="url(#mask0_2001_190)">
                    <path d="M27.5999 29.0256L23.3333 24.759V28.2H21.3333V21.3333H28.1999V23.3333H24.7256L28.9923 27.6L27.5999 29.0256ZM15.9999 28.6666C14.2477 28.6666 12.601 28.3342 11.0599 27.6693C9.51881 27.0044 8.17825 26.1021 7.03825 24.9623C5.89825 23.8225 4.99581 22.4822 4.33092 20.9413C3.66581 19.4004 3.33325 17.7541 3.33325 16.0023C3.33325 14.2503 3.6657 12.6035 4.33059 11.062C4.99547 9.52042 5.89781 8.17953 7.03759 7.03931C8.17736 5.89909 9.5177 4.99631 11.0586 4.33098C12.5995 3.66587 14.2458 3.33331 15.9976 3.33331C17.7496 3.33331 19.3964 3.66587 20.9379 4.33098C22.4795 4.99587 23.8204 5.89831 24.9606 7.03831C26.1008 8.17831 27.0036 9.51887 27.6689 11.06C28.334 12.6011 28.6666 14.2478 28.6666 16C28.6666 16.4018 28.6476 16.8034 28.6096 17.205C28.5716 17.6068 28.5145 18.0085 28.4383 18.4103H26.3846C26.4786 18.0085 26.549 17.6068 26.5959 17.205C26.643 16.8034 26.6666 16.4018 26.6666 16C26.6666 15.5155 26.6346 15.0312 26.5706 14.547C26.5064 14.0625 26.4059 13.5811 26.2693 13.1026H21.3126C21.3964 13.5811 21.4571 14.0625 21.4949 14.547C21.5325 15.0312 21.5513 15.5155 21.5513 16C21.5513 16.4018 21.5378 16.8034 21.5109 17.205C21.4838 17.6068 21.4434 18.0085 21.3896 18.4103H19.3896C19.4436 18.0085 19.484 17.6068 19.5109 17.205C19.5378 16.8034 19.5513 16.4018 19.5513 16C19.5513 15.5155 19.5325 15.0312 19.4949 14.547C19.4571 14.0625 19.3965 13.5811 19.3129 13.1026H12.6869C12.6034 13.5811 12.5427 14.0629 12.5049 14.548C12.4674 15.0331 12.4486 15.5182 12.4486 16.0033C12.4486 16.4882 12.4674 16.9721 12.5049 17.455C12.5427 17.9381 12.6034 18.4189 12.6869 18.8973H17.5896V20.8973H13.1459C13.4415 21.9462 13.823 22.9525 14.2906 23.9163C14.7584 24.8799 15.3281 25.7872 15.9999 26.6383C16.4017 26.6383 16.8034 26.6149 17.2049 26.568C17.6067 26.5209 18.0085 26.4751 18.4103 26.4306V28.459C18.0085 28.5034 17.6067 28.5491 17.2049 28.596C16.8034 28.6431 16.4017 28.6666 15.9999 28.6666ZM5.73059 18.8973H10.6873C10.6035 18.4189 10.5427 17.9371 10.5049 17.452C10.4674 16.9669 10.4486 16.4818 10.4486 15.9966C10.4486 15.5118 10.4674 15.0279 10.5049 14.545C10.5427 14.0619 10.6035 13.5811 10.6873 13.1026H5.73059C5.59392 13.5811 5.49347 14.0629 5.42925 14.548C5.36525 15.0331 5.33325 15.5182 5.33325 16.0033C5.33325 16.4882 5.36525 16.9721 5.42925 17.455C5.49347 17.9381 5.59392 18.4189 5.73059 18.8973ZM6.56925 11.1026H11.0863C11.3158 10.1351 11.6213 9.1992 12.0026 8.29498C12.3837 7.39053 12.8555 6.52642 13.4179 5.70265C11.9221 6.0342 10.5807 6.67176 9.39359 7.61531C8.20625 8.55887 7.26481 9.72131 6.56925 11.1026ZM13.4179 26.2973C12.8813 25.4649 12.4201 24.5965 12.0346 23.6923C11.649 22.7881 11.3349 21.8564 11.0923 20.8973H6.56925C7.26481 22.27 8.20836 23.4281 9.39992 24.3716C10.5915 25.3154 11.9308 25.9573 13.4179 26.2973ZM13.1459 11.1026H18.8539C18.5701 10.0531 18.1879 9.0512 17.7073 8.09698C17.2266 7.14298 16.6575 6.2312 15.9999 5.36165C15.3366 6.21276 14.766 7.11998 14.2883 8.08331C13.8105 9.04665 13.4297 10.0531 13.1459 11.1026ZM20.9076 11.1026H25.4306C24.735 9.72131 23.7936 8.55676 22.6063 7.60898C21.4191 6.66098 20.0777 6.02554 18.5819 5.70265C19.1186 6.53509 19.5733 7.40553 19.9459 8.31398C20.3186 9.22265 20.6391 10.1522 20.9076 11.1026Z" fill="#F5F5F5"/>
                </g>
                </svg>
    
                <a className="navbar-brand" href="https://www.cloudwaitress.com">
                    https://www.cloudwaitress.com
                </a> 
            </div>
            <div>
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <mask id="mask0_2001_196" style={{maskType: 'alpha'}} maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
                    <rect width="32" height="32" fill="#D9D9D9"/>
                </mask>
                <g mask="url(#mask0_2001_196)">
                    <path d="M11.3845 27.3334H7.07684C6.40328 27.3334 5.83317 27.1 5.3665 26.6334C4.89984 26.1667 4.6665 25.5966 4.6665 24.923V16C4.6665 14.4274 4.96395 12.9539 5.55884 11.5797C6.15373 10.2052 6.96228 9.00691 7.9845 7.98469C9.00673 6.96246 10.2051 6.15391 11.5795 5.55902C12.9537 4.96413 14.4272 4.66669 15.9998 4.66669C17.5725 4.66669 19.0459 4.96413 20.4202 5.55902C21.7946 6.15391 22.9929 6.96246 24.0152 7.98469C25.0374 9.00691 25.8459 10.2052 26.4408 11.5797C27.0357 12.9539 27.3332 14.4274 27.3332 16V24.923C27.3332 25.5966 27.0998 26.1667 26.6332 26.6334C26.1665 27.1 25.5964 27.3334 24.9228 27.3334H20.6152V17.8974H25.3332V16C25.3332 13.4 24.4276 11.1945 22.6165 9.38335C20.8054 7.57224 18.5998 6.66669 15.9998 6.66669C13.3998 6.66669 11.1943 7.57224 9.38317 9.38335C7.57206 11.1945 6.6665 13.4 6.6665 16V17.8974H11.3845V27.3334ZM9.3845 19.8974H6.6665V24.923C6.6665 25.0257 6.70928 25.1197 6.79484 25.205C6.88017 25.2906 6.97417 25.3334 7.07684 25.3334H9.3845V19.8974ZM22.6152 19.8974V25.3334H24.9228C25.0255 25.3334 25.1195 25.2906 25.2048 25.205C25.2904 25.1197 25.3332 25.0257 25.3332 24.923V19.8974H22.6152Z" fill="#F5F5F5"/>
                </g>
                </svg>
                <a className="navbar-brand" href="https://support.cloudwaitress.com/">
                    Contact Support
                </a> 
            
            </div> */}
            </MaintenanceFooter>
        </MaintenanceContainer>
    )
}